/**
 * Доска проекта: колонки с карточками и перенос задачи между ними.
 *
 * Как и в проектах, сгенерированный клиент отдаёт всё опциональным — protojson
 * не кладёт в ответ поля со значением по умолчанию. Приведение здесь, чтобы в
 * колонках и карточках не было ни `?? ''`, ни `?.` на каждом обращении.
 */
import { issueApi } from '@/api'
import { cdnUrl } from '@/api/cdn'
import {
  emptyFilters,
  hasAnyFilter,
  orNothing,
  type IssueFilters,
} from '@/features/issues/filters'
import type {
  V1IssueCard,
  V1IssueType,
  V1Priority,
  V1StatusCategory,
} from '@/api/clients/tracker-issue'

/** Метка задачи: имя и цвет из настроек проекта */
export interface BoardLabel {
  id: string
  name: string
  color: string
}

export interface BoardIssue {
  id: string
  /** Ключ вида DEV-1464 — то, чем задачу называют в разговоре */
  key: string
  title: string
  type: V1IssueType | ''
  priority: V1Priority | ''
  assigneeId: string
  assigneeName: string
  /** Ссылка на фото исполнителя; пусто — на карточке кружок с инициалами */
  assigneeAvatar: string
  /** Момент времени от сервера; на карточке от него остаётся день */
  dueDate: string
  /** Метки задачи; по ним же работает отбор в панели */
  labels: BoardLabel[]
  /**
   * Подзадачи: сколько всего и сколько закрыто. Ноль — их не заводили, и
   * счётчик на карточке не показывается вовсе.
   */
  subtasks: { total: number; done: number }
}

export interface BoardColumn {
  id: string
  name: string
  /** Цвет точки статуса; пусто — экран возьмёт цвет по категории */
  color: string
  statusCategory: V1StatusCategory | ''
  /** Сколько задач колонка рассчитана держать; 0 — без лимита */
  wipLimit: number
  issues: BoardIssue[]
  /**
   * Сколько задач колонки не приехало: длинная колонка обрезается страницей.
   * Счётчик в шапке — длина списка плюс это, иначе перенос карточки не менял
   * бы цифру до следующего обновления.
   */
  hidden: number
}

const toIssue = (card: V1IssueCard): BoardIssue => ({
  id: card.id ?? '',
  key: card.key ?? '',
  title: card.title ?? '',
  type: card.type ?? '',
  priority: card.priority ?? '',
  assigneeId: card.assigneeId ?? '',
  assigneeName: card.assigneeName ?? '',
  assigneeAvatar: cdnUrl(card.assigneeAvatarUrl),
  dueDate: card.dueDate ?? '',
  labels: (card.labels ?? []).map((label) => ({
    id: label.id ?? '',
    name: label.name ?? '',
    color: label.color ?? '',
  })),
  // Счётчики приходят строками: в protobuf это uint64.
  subtasks: { total: Number(card.childCount ?? 0), done: Number(card.childDoneCount ?? 0) },
})

/**
 * Отбор у доски и списка общий и живёт в `features/issues/filters`. Здесь он
 * только называется по-своему: экран доски знает его как `BoardFilters`.
 */
export type BoardFilters = IssueFilters

export { emptyFilters, hasAnyFilter }

export const getBoard = async (
  projectId: string,
  filters: BoardFilters = emptyFilters(),
): Promise<BoardColumn[]> => {
  const { data } = await issueApi.issueServiceGetBoard(
    projectId,
    orNothing(filters.assigneeIds),
    undefined,
    orNothing(filters.types),
    orNothing(filters.priorities),
    undefined,
    filters.search.trim() || undefined,
  )

  return (data.columns ?? []).map((entry) => {
    const issues = (entry.issues ?? []).map(toIssue)

    return {
      id: entry.column?.id ?? '',
      name: entry.column?.name ?? '',
      color: entry.column?.color ?? '',
      statusCategory: entry.column?.statusCategory ?? '',
      wipLimit: entry.column?.wipLimit ?? 0,
      issues,
      // Счётчик приходит строкой: в protobuf это int64, а он не влезает в number.
      hidden: Math.max(0, Number(entry.total ?? 0) - issues.length),
    }
  })
}

/** Перенос задачи между колонками — общий с списком, лежит рядом с задачами */
export { moveIssue } from '@/features/issues/api'
